import { useCallback } from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LockIcon from '@mui/icons-material/Lock'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import ListItem from '@mui/material/ListItem'
import Tooltip from '@mui/material/Tooltip'
import { CvsAutoComplete } from '@components'
import { colors } from '@global/constants'
import { getImageUrl } from '@utils/getImageUrl'
import { IntegrationMappableField, IntegrationMappingField, TemplateMappingOption } from '../utils'
import { InputHeader } from './input-header'
import { t } from '@lingui/core/macro'
import { MappingType } from '../utils'

const MarginIcon = styled(ArrowForwardIcon)`
  margin-bottom: 5px;
  margin-right: 12px;
  margin-left: 12px;
`

const ListItemPadding = styled(ListItem)`
  padding: 0;
  margin: 0;
  margin-bottom: 15px;
`

const RowContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
`

const MAX_MENU_HEIGHT = 155

interface Props {
  integrationSlug: string
  shouldShowInputHeader?: boolean
  integrationOptions: IntegrationMappableField[]
  templateOptions: TemplateMappingOption[]
  availableOptions: IntegrationMappableField[]
  availableTemplateOptions: TemplateMappingOption[]
  value: IntegrationMappingField
  onSelect: (value: IntegrationMappingField, index: number) => void
  onDelete: () => void
  integrationTitle: string
  mappingType: MappingType
  index: number
  mappingsDisabled?: boolean
}

export const FieldMapperRow = ({
  integrationSlug,
  shouldShowInputHeader = false,
  integrationOptions,
  availableOptions,
  templateOptions,
  availableTemplateOptions,
  value,
  onSelect,
  onDelete,
  integrationTitle,
  mappingType,
  index,
  mappingsDisabled,
}: Props) => {
  const required = !!value.required

  function handleClaravineFieldSelect(field: string | number) {
    const option = templateOptions.find(f => f.value == field)
    const updatedMapping: IntegrationMappingField = {
      ...value,
      cvFieldId: option ? field : '',
      fieldSourceType: option ? option.fieldSourceType : 'field',
    }
    onSelect(updatedMapping, index)
  }

  function handleIntegrationFieldSelect(field: string) {
    let mappingType: 'integration_field' | 'custom_field' = 'integration_field'

    if (field && field !== '') {
      const integrationField = integrationOptions.find(i => i.fieldName == field)

      if (integrationField) {
        mappingType = integrationField.type
      }
    }

    const updatedMapping: IntegrationMappingField = {
      ...value,
      fieldName: field || '',
      type: mappingType,
    }
    onSelect(updatedMapping, index)
  }

  const buildIntegrationImageUrl = useCallback(() => {
    return getImageUrl(integrationSlug)
  }, [integrationTitle, integrationSlug])

  return (
    <ListItemPadding>
      <RowContainer>
        {mappingType == 'inbound' ? (
          <section css={mappingsContainer}>
            <div css={inputContainer}>
              {shouldShowInputHeader && (
                <InputHeader text="Inbound" imageUrl={buildIntegrationImageUrl()} />
              )}
              <CvsAutoComplete
                key={`from-${value.fieldName}`}
                margin="dense"
                options={
                  availableOptions.map(i => {
                    return {
                      label: i.displayName,
                      value: i.fieldName,
                    }
                  }) as never[]
                }
                allOptions={
                  integrationOptions.map(i => {
                    return {
                      label: i.displayName,
                      value: i.fieldName,
                    }
                  }) as never[]
                }
                onSelected={handleIntegrationFieldSelect}
                selected={value.fieldName}
                label={`Map from ${integrationTitle}`}
                disabled={required || mappingsDisabled}
                isClearable={!required || !mappingsDisabled}
                error={
                  value.fieldName == '' || value.fieldName == null ? t`Please select a field` : ''
                }
              />
            </div>
            <MarginIcon style={{ height: '40px', color: colors.gray.main }} />
            <div css={inputContainer}>
              {shouldShowInputHeader && (
                <InputHeader text="Claravine" imageUrl={getImageUrl('c_logo_green_light', 'svg')} />
              )}
              <CvsAutoComplete
                key={`to-${value.fieldName}`}
                margin="dense"
                options={availableTemplateOptions as never[]}
                allOptions={templateOptions as never[]}
                onSelected={handleClaravineFieldSelect}
                selected={String(value.cvFieldId)}
                label={t`Map to Claravine`}
                disabled={mappingsDisabled}
                isClearable={!mappingsDisabled}
                error={
                  value.cvFieldId == '' || value.cvFieldId == null ? t`Please select a field` : ''
                }
                helperTextStyles={{
                  marginLeft: '0px',
                }}
              />
            </div>
            <FieldMapperRowIcon required={required} onDelete={onDelete} />
          </section>
        ) : (
          <section css={mappingsContainer}>
            <div css={inputContainer}>
              {shouldShowInputHeader && (
                <InputHeader
                  text={t`Claravine`}
                  imageUrl={getImageUrl('c_logo_green_light', 'svg')}
                />
              )}
              <CvsAutoComplete
                style={{ width: 450 }}
                key={`to-${value.fieldName}`}
                margin="dense"
                options={availableTemplateOptions as never[]}
                allOptions={templateOptions as never[]}
                onSelected={handleClaravineFieldSelect}
                selected={String(value.cvFieldId)}
                label={t`Map from Claravine`}
                maxMenuHeight={MAX_MENU_HEIGHT}
                disabled={mappingsDisabled}
                isClearable={!mappingsDisabled}
                error={
                  value.cvFieldId == '' || value.cvFieldId == null ? t`Please select a field` : ''
                }
              />
            </div>
            <MarginIcon style={{ height: '40px', color: colors.gray.main }} />
            <div css={inputContainer}>
              {shouldShowInputHeader && (
                <InputHeader text={t`Outbound`} imageUrl={buildIntegrationImageUrl()} />
              )}
              <CvsAutoComplete
                key={`from-${value.fieldName}`}
                margin="dense"
                options={
                  availableOptions.map(i => {
                    return {
                      label: i.displayName,
                      value: i.fieldName,
                    }
                  }) as never[]
                }
                allOptions={
                  integrationOptions.map(i => {
                    return {
                      label: i.displayName,
                      value: i.fieldName,
                    }
                  }) as never[]
                }
                onSelected={handleIntegrationFieldSelect}
                selected={value.fieldName}
                label={`Map to ${integrationTitle}`}
                maxMenuHeight={MAX_MENU_HEIGHT}
                disabled={required || mappingsDisabled}
                isClearable={!required || !mappingsDisabled}
                error={
                  value.fieldName == '' || value.fieldName == null ? 'Please select a field' : ''
                }
              />
            </div>
            <FieldMapperRowIcon required={required} onDelete={onDelete} />
          </section>
        )}
      </RowContainer>
    </ListItemPadding>
  )
}

const deleteIcon = css`
  margin-bottom: 15px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  color: #757575;
  width: 25;
  cursor: pointer;
`

const lockIcon = css`
  margin-bottom: 15px;
  margin-left: 12px;
  display: flex;
  align-items: center;
  color: #757575;
  width: 25;
`

const iconConatiner = css`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  min-width: 35px;
`

const mappingsContainer = css`
  display: flex;
  align-items: end;
`

const inputContainer = css`
  width: 450px;

  > div > span {
    position: absolute;
  }
`

const FieldMapperRowIcon = ({ required, onDelete }) => (
  <div css={iconConatiner}>
    {!required ? (
      <div css={deleteIcon} onClick={onDelete}>
        <Tooltip placement="top" title={t`Remove mapping`} arrow>
          <RemoveCircleIcon />
        </Tooltip>
      </div>
    ) : (
      <div css={lockIcon}>
        <Tooltip placement="top" title={t`Required mapping`} arrow>
          <LockIcon />
        </Tooltip>
      </div>
    )}
  </div>
)
